/**
 * SolarDatabase - Solar Equipment Database
 *
 * Full CRUD interface for managing solar panel/module equipment in the database.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Alert } from '@mui/material';
import { WbSunny as SolarIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { vortxColors } from '@vortx/shared-ui';
import { useAuth } from '@vortx/shared-ui';
import EquipmentTable from '../EquipmentTable';
import EquipmentForm from './EquipmentForm';
import { API_BASE_URL } from '../../../services/api';

const SOLAR_COLUMNS = [
    { id: 'manufacturer', label: 'Manufacturer', width: '18%' },
    { id: 'model', label: 'Model', width: '18%' },
    { id: 'wattage_per_panel', label: 'Wattage (W)', width: '12%', highlight: true, render: (v) => v?.toLocaleString() },
    { id: 'efficiency_percent', label: 'Efficiency (%)', width: '12%', render: (v) => v ? `${v.toFixed(1)}%` : '-' },
    { id: 'turnkey_cost_per_kw', label: 'Turnkey ($/kW)', width: '14%', highlight: true, render: (v) => v ? `$${v.toLocaleString()}` : '-' },
    { id: 'status', label: 'Status', width: '10%' }
];

function SolarDatabase() {
    const { currentUser } = useAuth();
    const [equipment, setEquipment] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [editingEquipment, setEditingEquipment] = useState(null);

    const fetchEquipment = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/equipment/solar?status=active`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Failed to fetch solar equipment');
            const data = await res.json();

            // Sort alphabetically by manufacturer, then by model
            const sorted = (data.equipment || []).sort((a, b) => {
                const manufacturerCompare = (a.manufacturer || '').localeCompare(b.manufacturer || '');
                if (manufacturerCompare !== 0) return manufacturerCompare;
                return (a.model || '').localeCompare(b.model || '');
            });

            setEquipment(sorted);
        } catch (err) {
            console.error('[SolarDatabase] Error fetching equipment:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        fetchEquipment();
    }, [fetchEquipment]);

    const handleCreate = () => {
        setEditingEquipment(null);
        setShowForm(true);
    };

    const handleEdit = (item) => {
        setEditingEquipment(item);
        setShowForm(true);
    };

    const handleCopy = (item) => {
        // Copy item data but remove id and metadata fields
        const { id, created_at, created_by, created_by_email, updated_at, version_history, ...copiedData } = item;
        setEditingEquipment(copiedData);
        setShowForm(true);
    };

    const handleSave = async (data) => {
        try {
            // Clean data: remove metadata fields and convert string numbers to actual numbers
            const { id, created_at, created_by, created_by_email, updated_at, version_history, ...cleanedData } = data;
            const numericFields = ['wattage_per_panel', 'efficiency_percent', 'turnkey_cost_per_kw',
                                   'panel_weight_kg', 'degradation_rate_annual', 'warranty_years',
                                   'dc_power_output_stc', 'max_system_voltage', 'dc_current_output'];
            numericFields.forEach(field => {
                if (cleanedData[field] !== null && cleanedData[field] !== undefined && cleanedData[field] !== '') {
                    cleanedData[field] = Number(cleanedData[field]);
                }
            });

            const token = await currentUser.getIdToken();
            const url = editingEquipment?.id
                ? `${API_BASE_URL}/api/site_dna/v1/equipment/solar/${editingEquipment.id}`
                : `${API_BASE_URL}/api/site_dna/v1/equipment/solar`;
            const method = editingEquipment?.id ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(cleanedData)
            });

            if (!res.ok) throw new Error('Failed to save equipment');

            setShowForm(false);
            setEditingEquipment(null);
            fetchEquipment();
        } catch (err) {
            console.error('[SolarDatabase] Error saving equipment:', err);
            alert(`Error: ${err.message}`);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Delete ${item.manufacturer} ${item.model}?`)) return;

        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/equipment/solar/${item.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) throw new Error('Failed to delete equipment');
            fetchEquipment();
        } catch (err) {
            console.error('[SolarDatabase] Error deleting equipment:', err);
            alert(`Error: ${err.message}`);
        }
    };

    return (
        <Box>
            <Alert
                severity="info"
                icon={<SolarIcon />}
                sx={{
                    mb: 3,
                    bgcolor: alpha(vortxColors.gold, 0.1),
                    color: vortxColors.white,
                    '& .MuiAlert-icon': { color: vortxColors.gold }
                }}
            >
                Manage solar panel/module specifications and pricing. Turnkey cost includes installation.
            </Alert>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <EquipmentTable
                title="Solar Modules"
                description="Manage solar panel specifications and pricing."
                columns={SOLAR_COLUMNS}
                data={equipment}
                loading={loading}
                onAdd={handleCreate}
                onEdit={handleEdit}
                onCopy={handleCopy}
                onDelete={handleDelete}
                onRefresh={fetchEquipment}
                emptyMessage="No solar modules in database. Click 'Add Entry' to create one."
            />

            {showForm && (
                <EquipmentForm
                    open={showForm}
                    type="solar"
                    equipment={editingEquipment}
                    onSave={handleSave}
                    onCancel={() => {
                        setShowForm(false);
                        setEditingEquipment(null);
                    }}
                />
            )}
        </Box>
    );
}

export default SolarDatabase;
